import { Fragment, ReactNode, useEffect, useState } from "react";
import styled from "styled-components";
import { Button, Divider, Input, Modal, message } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { putPostReferenceText } from "../../api/postAPI";

interface Props {
  postId: number;
  // 게시글 상세 조회로 받은 참고문헌
  referenceText?: string | null;
  // 참고문헌 수정 가능 여부 (서버에서도 다시 검사)
  canEdit?: boolean;
  // 좁은 곳(자문 게시글 패널)에 넣을 때
  compact?: boolean;
}

const MAX_LENGTH = 30000;

// 주소 / doi / PMID / PMCID 를 찾는다 (앞에서부터 처음 맞는 것)
// 1: 주소, 2-3: doi 접두어-값, 4-5: PMID 접두어-값, 6-7: PMCID 접두어-값
const LINK_PATTERN =
  /(https?:\/\/[^\s<>"]+)|\b(doi:?\s*)(10\.\d{4,9}\/[^\s<>"]+)|\b(PMID:?\s*)(\d{1,9})\b|\b(PMCID:?\s*)(PMC\d{1,9})\b/gi;

// 문장 끝 마침표, 쉼표, 짝이 안 맞는 닫는 괄호는 링크에서 뺀다
const trimTrailing = (value: string): string => {
  let s = value;
  for (;;) {
    const last = s.charAt(s.length - 1);
    if (/[.,;:!?'"]/.test(last)) {
      s = s.slice(0, -1);
    } else if (last === ")" && s.split("(").length < s.split(")").length) {
      s = s.slice(0, -1);
    } else if (last === "]" && s.split("[").length < s.split("]").length) {
      s = s.slice(0, -1);
    } else {
      return s;
    }
  }
};

const doiUrl = (doi: string) => `https://doi.org/${doi.split("/").map(encodeURIComponent).join("/")}`;

// 한 줄의 텍스트를 글자 / 링크 조각으로 나눈다. HTML 로 넣지 않으므로 텍스트 안의 태그는 글자 그대로 보인다.
export const linkifyReferenceLine = (line: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  const pattern = new RegExp(LINK_PATTERN.source, LINK_PATTERN.flags);
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(line)) !== null) {
    let label: string;
    let href: string;
    if (match[1]) {
      label = trimTrailing(match[1]);
      href = label;
    } else if (match[3]) {
      const doi = trimTrailing(match[3]);
      label = match[2] + doi;
      href = doiUrl(doi);
    } else if (match[5]) {
      label = match[4] + match[5];
      href = `https://pubmed.ncbi.nlm.nih.gov/${match[5]}/`;
    } else {
      label = match[6] + match[7];
      href = `https://pmc.ncbi.nlm.nih.gov/articles/${match[7].toUpperCase()}/`;
    }
    // 잘라낸 문장부호는 다음 글자 조각으로 넘긴다
    pattern.lastIndex = match.index + label.length;

    if (match.index > cursor) nodes.push(line.slice(cursor, match.index));
    nodes.push(
      <a key={match.index} href={href} target="_blank" rel="noopener noreferrer">
        {label}
      </a>
    );
    cursor = match.index + label.length;
  }
  if (cursor < line.length) nodes.push(line.slice(cursor));
  return nodes;
};

// "1.", "2)", "[3]" 처럼 번호로 시작하는 줄은 둘째 줄부터 들여쓴다
const NUMBERED_LINE = /^\s*\[?\d{1,3}[.)\]]/;

// 게시글 참고문헌 (본문 아래, 첨부파일 위. 게시글당 자유 텍스트 하나)
const PostReferences = ({ postId, referenceText: initialText, canEdit = false, compact = false }: Props) => {
  const [text, setText] = useState<string | null>(initialText ?? null);
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setText(initialText ?? null);
  }, [postId, initialText]);

  // 읽기만 하는 사용자에게는 참고문헌이 없으면 영역을 숨긴다
  if (!canEdit && !text) return null;

  const openModal = () => {
    setDraft(text ?? "");
    setModalOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await putPostReferenceText(postId, draft);
      setText(saved);
      setModalOpen(false);
      message.success(saved ? "참고문헌을 저장했습니다." : "참고문헌을 삭제했습니다.");
    } catch (e: any) {
      message.error(e?.response?.data?.message || "참고문헌을 저장하지 못했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrapper $compact={compact}>
      {compact ? <CompactTitle>References</CompactTitle> : <Divider style={{ fontSize: "1.4rem" }}>References</Divider>}
      <Body $compact={compact}>
        {canEdit && (
          <Header>
            <span>참고문헌</span>
            <Button size="small" icon={text ? <EditOutlined /> : <PlusOutlined />} onClick={openModal}>
              {text ? "수정" : "등록"}
            </Button>
          </Header>
        )}
        {text ? (
          <ReferenceText $compact={compact}>
            {text.split("\n").map((line, i) => (
              <Line key={i} $hanging={NUMBERED_LINE.test(line)}>
                {line ? linkifyReferenceLine(line).map((node, j) => <Fragment key={j}>{node}</Fragment>) : " "}
              </Line>
            ))}
          </ReferenceText>
        ) : (
          <Empty>등록된 참고문헌이 없습니다.</Empty>
        )}
      </Body>

      <Modal
        open={modalOpen}
        title="참고문헌"
        width={800}
        okText="저장"
        cancelText="취소"
        confirmLoading={saving}
        onOk={save}
        onCancel={() => setModalOpen(false)}
      >
        <Help>
          참고문헌 목록을 그대로 붙여 넣으세요. 줄바꿈은 그대로 보이고, 주소(http/https)와 doi, PMID, PMCID 는 해당
          페이지로 가는 링크가 됩니다. 모두 지우고 저장하면 참고문헌이 삭제됩니다.
        </Help>
        <Input.TextArea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoSize={{ minRows: 10, maxRows: 24 }}
          maxLength={MAX_LENGTH}
          placeholder={
            "1. Louis DN, et al. The 2021 WHO Classification of Tumors of the Central Nervous System: a summary. Neuro Oncol. 2021;23(8):1231-51. PMID: 34185076 · doi:10.1093/neuonc/noab106\n2. ..."
          }
          autoFocus
        />
        {/* antd showCount 는 입력창 아래로 떠서 모달 버튼과 겹치므로 직접 표시한다 */}
        <Counter>
          {draft.length.toLocaleString()} / {MAX_LENGTH.toLocaleString()}
        </Counter>
      </Modal>
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $compact: boolean }>`
  width: 100%;
  margin-bottom: ${({ $compact }) => ($compact ? "1rem" : "2rem")};
`;

const Body = styled.div<{ $compact: boolean }>`
  width: ${({ $compact }) => ($compact ? "100%" : "90%")};
  max-width: 100rem;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
  font-weight: bold;
`;

const CompactTitle = styled.div`
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #595959;
`;

const ReferenceText = styled.div<{ $compact: boolean }>`
  font-size: ${({ $compact }) => ($compact ? "0.8rem" : "0.9rem")};
  line-height: 1.6;
  color: #262626;

  a {
    color: #1677ff;
  }
  a:hover {
    text-decoration: underline;
  }
`;

const Line = styled.div<{ $hanging: boolean }>`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  ${({ $hanging }) => ($hanging ? "padding-left: 2em; text-indent: -2em; margin-bottom: 0.3em;" : "")}
`;

const Empty = styled.div`
  padding: 8px 0;
  text-align: center;
  color: #8c8c8c;
`;

const Counter = styled.div`
  margin-top: 4px;
  text-align: right;
  font-size: 0.8rem;
  color: #8c8c8c;
`;

const Help = styled.p`
  margin-bottom: 8px;
  font-size: 0.85rem;
  color: #8c8c8c;
`;

export default PostReferences;
